// ============================================================
// PlainTargetTestRow - clickable derived tests for targets NOT
// spawned from a Character: Dodge (vs dodge.agility), Parry
// (vs parry.weaponSkill), Toughness (vs TB×10). Deliberate
// tests - rollable even when canDodge/canParry are false (those
// flags govern defence-chain reactions); a dim tag notes it.
// ============================================================
import { memo } from 'react';
import styles from '../styles/ActionSheet.module.css';
import { PLAIN_TARGET_TEST_DEFS, calcPlainTargetTestBase } from '../engine/characteristicTests';
import type { PlainTargetTestId } from '../engine/characteristicTests';
import type { Target } from '../types';

interface PlainTargetTestRowProps {
  target: Target;
  onRoll: (testId: PlainTargetTestId) => void;
  /** Right-click: opposed vs the acting character. Absent = inert. */
  onOpposed?: ((testId: PlainTargetTestId) => boolean) | null;
  /**
   * Tests a CONDITION forbids, keyed by id, the value being the reason.
   * Distinct from the untrained tag below: untrained is a note on a roll
   * you may still make, this is a roll you may not.
   */
  blocked?: Record<string, string>;
  /** Condition penalty per test id - the SAME value handed to `onRoll`. */
  conditionMods?: Record<string, number>;
}

function noReaction(target: Target, testId: PlainTargetTestId): boolean {
  if (testId === 'dodge') return !target.dodge.canDodge;
  if (testId === 'parry') return !target.parry.canParry;
  return false;
}

function PlainTargetTestRow({
  target,
  onRoll,
  onOpposed = null,
  blocked = {},
  conditionMods = {},
}: PlainTargetTestRowProps) {
  return (
    <div className={styles.skillRow} data-testid="plain-target-test-row">
      {PLAIN_TARGET_TEST_DEFS.map((def) => (
        <TestButton
          key={def.id}
          def={def}
          target={target}
          condMod={conditionMods[def.id] ?? 0}
          block={blocked[def.id]}
          onRoll={onRoll}
          onOpposed={onOpposed}
        />
      ))}
    </div>
  );
}

interface TestButtonProps {
  def: (typeof PLAIN_TARGET_TEST_DEFS)[number];
  target: Target;
  condMod: number;
  /** Why a condition forbids this test, if it does. */
  block: string | undefined;
  onRoll: PlainTargetTestRowProps['onRoll'];
  onOpposed: PlainTargetTestRowProps['onOpposed'];
}

/** One test: click rolls it, right-click rolls it opposed; a forbidden test is disabled with its reason. */
function TestButton({ def, target, condMod, block, onRoll, onOpposed }: TestButtonProps) {
  const base = calcPlainTargetTestBase(target, def.id) + condMod;
  return (
    <button
      type="button"
      className={styles.skillBtn}
      disabled={!!block}
      title={block ?? `Roll a ${def.label} test (d100 vs ${base} + modifiers)`}
      aria-label={`Roll ${def.label} test`}
      onClick={() => onRoll(def.id)}
      onContextMenu={(e) => {
        if (!onOpposed) return;
        e.preventDefault();
        onOpposed(def.id);
      }}
    >
      <span className={styles.charLabel}>{def.label}</span>
      <span>
        {block ? (
          <span className={styles.noReactionTag} data-testid={`blocked-${def.id}`}>
            {block}
          </span>
        ) : (
          <TestTags target={target} testId={def.id} condMod={condMod} />
        )}{' '}
        <span className={styles.charValue}>{base}</span>
      </span>
    </button>
  );
}

/** "no reaction" for an untrained dodge or parry, and the condition modifier when there is one. */
function TestTags({ target, testId, condMod }: { target: Target; testId: PlainTargetTestId; condMod: number }) {
  return (
    <>
      {noReaction(target, testId) && <span className={styles.noReactionTag}>no reaction</span>}
      {condMod !== 0 && (
        <span className={styles.noReactionTag} data-testid={`cond-mod-${testId}`}>
          {condMod > 0 ? '+' : '\u2212'}
          {Math.abs(condMod)}
        </span>
      )}
    </>
  );
}

export default memo(PlainTargetTestRow);
